import React, { memo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  Avatar,
  Chip,
  CircularProgress,
  Autocomplete,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Group as GroupIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  LocalOffer as TagIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import FormActionBar from '../../../shared/components/FormActionBar';
import { EntityListPanel, EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import OperationalCallChainPanel from './OperationalCallChainPanel';
import EscalationChainTabContent from './EscalationChainTabContent';
import { usePortalUserForm } from '../hooks/usePortalUserForm';
import { useContactsTab } from '../hooks/useContactsTab';
import type { FormState, FormAction } from '../hooks/usePortalUserForm';
import type { ContactWithUser } from '../../../types';

interface PortalUserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  customerContactTypes: string[];
}

const contactListConfig: EntityListItemConfig<ContactWithUser> = {
  getPrimaryText: (c) => c.name || c.email,
  getSecondaryText: (c) => {
    const parts: string[] = [];
    if (c.contact_types.length > 0) parts.push(c.contact_types.join(', '));
    if (c.role) parts.push(c.role);
    return parts.join(' \u2022 ') || c.email;
  },
  getId: (c) => c.username,
  getSearchText: (c) => `${c.name} ${c.email} ${c.role} ${c.contact_types.join(' ')}`,
};

const PortalUserManagementModal: React.FC<PortalUserManagementModalProps> = ({
  isOpen,
  onClose,
  customerId,
  customerName,
  customerContactTypes,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (isOpen) setActiveTab(0);
  }, [isOpen]);

  const {
    form: inviteForm,
    dispatchForm: dispatchInviteForm,
    viewMode,
    setViewMode,
    inviting,
    accessDenied,
    error,
    setError,
    deleteTarget,
    setDeleteTarget,
    snackbar: userSnackbar,
    closeSnackbar: closeUserSnackbar,
    setHasAttemptedInvite,
    emailError,
    nameError,
    handleInvite,
    handleDelete,
    handleResetPassword,
    handleResendInvite,
  } = usePortalUserForm(isOpen, customerId);

  const {
    form: contactForm,
    dispatchForm: dispatchContactForm,
    contacts,
    selectedContact,
    selectContact,
    clearSelection,
    loadContacts,
    loading,
    saving,
    snackbar: contactSnackbar,
    closeSnackbar: closeContactSnackbar,
    handleSaveMetadata,
    handleReorderOperational,
  } = useContactsTab(isOpen, customerId);

  const operationalContacts = contacts
    .filter((c) => c.contact_types.includes('operational') && c.operational_priority !== null)
    .sort((a, b) => (a.operational_priority ?? 0) - (b.operational_priority ?? 0));

  if (accessDenied) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <ModalHeader icon={GroupIcon} title="Portal Users" onClose={onClose} />
        <DialogContent>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography color="text.secondary">
              You need admin permissions to manage portal users.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        slotProps={{ paper: { sx: { height: '80vh', maxHeight: '80vh' } } }}
      >
        <ModalHeader
          icon={GroupIcon}
          title="Portal Users"
          onClose={onClose}
          desktopRecommended
          subtitle={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Customer: <strong>{customerName}</strong>
            </Typography>
          }
        />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="Users & Contacts" icon={<GroupIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab label="Escalation Chain" icon={<AccountTreeIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
          {activeTab === 1 ? (
            <EscalationChainTabContent
              isActive={isOpen && activeTab === 1}
              customerId={customerId}
              contacts={contacts}
            />
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Left panel — user list */}
              <Box
                sx={{
                  width: 300,
                  borderRight: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.default',
                }}
              >
                <EntityListPanel
                  entities={contacts}
                  selectedEntity={viewMode === 'list' ? selectedContact : null}
                  isNew={false}
                  saving={false}
                  config={contactListConfig}
                  addButtonText="Invite User"
                  emptyStateText="No portal users yet"
                  emptyStateHelperText="Invite users to get started"
                  onSelect={(contact) => {
                    selectContact(contact);
                    setViewMode('list');
                  }}
                  onAddNew={() => {
                    dispatchInviteForm({ type: 'RESET' });
                    setHasAttemptedInvite(false);
                    setViewMode('invite');
                  }}
                  searchable
                  isLoading={false}
                />
              </Box>

              {/* Right panel */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                {error && (
                  <InlineAlert severity="error" onClose={() => setError(null)} sx={{ m: 3, mb: 0 }}>
                    {error}
                  </InlineAlert>
                )}

                {viewMode === 'invite' ? (
                  <InviteForm
                    form={inviteForm}
                    dispatchForm={dispatchInviteForm}
                    emailError={emailError}
                    nameError={nameError}
                    inviting={inviting}
                    onInvite={async () => {
                      const ok = await handleInvite();
                      if (ok) await loadContacts();
                    }}
                    onCancel={() => setViewMode('list')}
                  />
                ) : selectedContact ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* User identity header */}
                    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Avatar sx={{ width: 48, height: 48, fontSize: '1.25rem' }}>
                          {selectedContact.name?.charAt(0) || selectedContact.email.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{selectedContact.name || selectedContact.email}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedContact.email}
                          </Typography>
                        </Box>
                        <Chip
                          label={selectedContact.status}
                          size="small"
                          color={selectedContact.status === 'CONFIRMED' ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.75rem', ml: 'auto' }}
                        />
                      </Box>
                    </Box>

                    {/* Contact type tags strip */}
                    <Box sx={{ px: 3, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={customerContactTypes}
                        value={contactForm.contact_types}
                        onChange={(_, newValue) => dispatchContactForm({ type: 'SET_TYPES', payload: newValue as string[] })}
                        disableCloseOnSelect
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const hasPrefix = typeof option === 'string' && option.includes(':');
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={key}
                                {...tagProps}
                                label={option}
                                size="small"
                                color={hasPrefix ? 'primary' : 'default'}
                                variant={hasPrefix ? 'filled' : 'outlined'}
                                icon={hasPrefix ? <TagIcon fontSize="small" /> : undefined}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={contactForm.contact_types.length === 0 ? 'Add contact types...' : 'Add type...'}
                            variant="standard"
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                          />
                        )}
                      />
                    </Box>

                    {/* Contact metadata fields */}
                    <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 480 }}>
                      <TextField
                        label="Display Name"
                        value={contactForm.name}
                        onChange={(e) => dispatchContactForm({ type: 'SET_NAME', payload: e.target.value })}
                        size="small"
                        placeholder="Full name"
                      />

                      <TextField
                        label="Role"
                        value={contactForm.role}
                        onChange={(e) => dispatchContactForm({ type: 'SET_ROLE', payload: e.target.value })}
                        size="small"
                        placeholder="e.g. CTO, IT Manager"
                      />

                      <TextField
                        label="Phone Number"
                        value={contactForm.phone_number}
                        onChange={(e) => dispatchContactForm({ type: 'SET_PHONE', payload: e.target.value })}
                        size="small"
                        placeholder="+45 12345678"
                      />
                    </Box>

                    {/* Single action bar at bottom */}
                    <Box sx={{ px: 3, pb: 3 }}>
                      <FormActionBar
                        onSave={handleSaveMetadata}
                        saving={saving}
                        onDelete={() => setDeleteTarget(selectedContact as never)}
                        deleteLabel="Delete User"
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleResendInvite(selectedContact as never)}
                          disabled={saving}
                        >
                          Resend Invite
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleResetPassword(selectedContact as never)}
                          disabled={saving}
                        >
                          Reset Password
                        </Button>
                      </FormActionBar>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: 1 }}>
                      <EmptyStateMessage
                        icon={PersonIcon}
                        title="No user selected"
                        description="Select a user to view details or invite a new one"
                      />
                    </Box>

                    {operationalContacts.length > 0 && (
                      <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                        <OperationalCallChainPanel
                          contacts={operationalContacts}
                          onReorder={handleReorderOperational}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {selectedContact && operationalContacts.length > 0 && (
                  <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                    <OperationalCallChainPanel
                      contacts={operationalContacts}
                      onReorder={handleReorderOperational}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <ModalFooter count={contacts.length} label={`portal user${contacts.length !== 1 ? 's' : ''}`} onClose={onClose} />
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        title="Delete Portal User"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          const ok = await handleDelete();
          if (ok) {
            clearSelection();
            await loadContacts();
          }
        }}
      >
        <Typography>
          Are you sure you want to delete <strong>{deleteTarget?.email}</strong>?
          This action cannot be undone.
        </Typography>
      </DeleteConfirmDialog>

      <SnackbarAlert
        snackbar={userSnackbar.open ? userSnackbar : contactSnackbar}
        onClose={userSnackbar.open ? closeUserSnackbar : closeContactSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
};

interface InviteFormProps {
  form: FormState;
  dispatchForm: React.Dispatch<FormAction>;
  emailError?: string;
  nameError?: string;
  inviting: boolean;
  onInvite: () => void;
  onCancel: () => void;
}

const InviteForm: React.FC<InviteFormProps> = ({
  form,
  dispatchForm,
  emailError,
  nameError,
  inviting,
  onInvite,
  onCancel,
}) => (
  <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
    <Typography variant="h6" sx={{ mb: 3 }}>
      Invite User
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
      <TextField
        label="Email"
        type="email"
        value={form.email}
        onChange={(e) => dispatchForm({ type: 'SET_FIELD', field: 'email', payload: e.target.value })}
        error={!!emailError}
        helperText={emailError}
        required
      />
      <TextField
        label="Name"
        value={form.name}
        onChange={(e) => dispatchForm({ type: 'SET_FIELD', field: 'name', payload: e.target.value })}
        error={!!nameError}
        helperText={nameError}
        required
      />
    </Box>

    <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
      <Button
        variant="contained"
        onClick={onInvite}
        disabled={inviting}
        startIcon={<PersonAddIcon />}
      >
        {inviting ? 'Sending...' : 'Send Invite'}
      </Button>
      <Button variant="outlined" onClick={onCancel} disabled={inviting}>
        Cancel
      </Button>
    </Box>
  </Box>
);

export default memo(PortalUserManagementModal);
